import { Component, Inject, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, QueryList, ViewChildren, TemplateRef, PLATFORM_ID } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { isPlatformBrowser, formatDate } from '@angular/common';
import { PatientLabService } from '../../../core/services/patient-lab.service';
import { AiAssistantService } from '../../../core/services/ai-assistant.service';
import { PatientService } from '../../../core/services/patient.service';
import { PatientCommunicationService } from '../../../core/services/patient-communication.service';
import { PdfDownloadService } from '../../../core/services/pdf-download.service';
import { SpeechRecognitionService } from '../../../core/services/speech-recognition.service';
import { UserService } from '../../../core/services/user.service';
import { GroupService } from '../../../core/services/group.service';
import { isStateModeGroup } from '../../util/state-mode.util';
import { Subscription, Observable, BehaviorSubject, catchError, debounceTime, delay, map, of, retryWhen, startWith, switchMap, takeUntil, tap, throwError, timeout } from 'rxjs';
import { BaseComponent } from '../../../core/components/base/base.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import { MatDialog } from '@angular/material/dialog';
import { SocketService } from '../../../core/services/socket.service';
import { TokenUsageService } from '../../../core/services/token-usage.service';
import { PatientNoteService } from '../../../core/services/patient-note.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { WhatsAppChatDialogComponent } from '../whatsapp-chat-dialog/whatsapp-chat-dialog.component';
import { NoteTypeService } from '../../../core/services/note-type.service';
import { FileObject } from './../../../core/interfaces/file-object';
import { ColleagueService } from './../../../core/services/colleague.service';
import { ProcedureCode } from './../../../core/services/app-data.service';
import { FileDownloadService } from './../../../core/services/file-download.service';
import { ClipboardService } from './../../../core/services/clipboard.service';
import * as moment from 'moment';

import { ResultsService } from '../../../core/services/results.service';
import {
  labResultsDialogCapabilities,
  LabResultsDialogCapabilities,
  unlinkedBlobErrorMessage,
  unlinkedPdfFileName,
  UnlinkedResultDetail,
  unlinkedResultsDisplayState,
  UnlinkedResultsDisplayState,
  unlinkedResultsStateCopy
} from '../../../core/models/unlinked-result.model';
import {
  vendorDoctorBadges,
  VendorDoctorBadge,
  VendorDoctors
} from '../../../core/models/vendor-doctors.model';

import { environment } from '../../../../environments/environment';
export interface LabResultsDialogData {
  /** Absent in read-only mode: an unlinked result has no PatientLabOrder. */
  labOrder?: any;
  /** Absent in read-only mode: an unlinked result has not been attached to a patient yet. */
  patient?: any;
  /**
   * Read-only view of an inbound pathology result that arrived without a MedicAI lab order.
   * Turns off every patient-bound and outbound capability - see `labResultsDialogCapabilities`.
   */
  readOnly?: boolean;
  /** The parsed document, already fetched from `GET .../unlinked/:id/results`. */
  unlinkedResult?: UnlinkedResultDetail | null;
  /** Group that owns the unlinked result; needed for the download endpoint. */
  groupId?: string | null;
  /**
   * The four parties the lab named, for the read-only path ONLY.
   *
   * `GET .../unlinked/:id/results` returns the parsed DOCUMENT and knows nothing about the
   * parties, so the caller hands over the row it already has. For a normal lab order this is
   * left absent and the badges come off `labOrder.vendorDoctors`.
   *
   * Display only. It touches nothing that `readOnly` gates — `labResultsDialogCapabilities`
   * still decides what this dialog may DO, entirely on its own.
   */
  vendorDoctors?: VendorDoctors | null;
  /**
   * OUR OWN vendor code, from the ENVELOPE of whichever list the caller loaded. The slot whose
   * `code` equals it is not rendered. Absent means suppress nothing, never "suppress the
   * default" — the client has no default.
   */
  vendorDoctorPlatformCode?: string | null;
}

@Component({
  selector: 'app-lab-results-dialog',
  templateUrl: './lab-results-dialog.component.html',
  styleUrls: ['./lab-results-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LabResultsDialogComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('messagesContainer') messagesContainer?: ElementRef;
  @ViewChild('addNoteDialog') addNoteDialog?: TemplateRef<any>;
  @ViewChildren(MatMenuTrigger) menuTriggers!: QueryList<MatMenuTrigger>;

  labOrder: any;
  patient: any;
  labResults: any = null;
  loading = false;
  error: string | null = null;

  // ---- Read-only (unlinked result) mode ----
  /** True when showing an inbound result that has no lab order and no patient. */
  readOnly = false;
  /**
   * Single source of truth for what this dialog may do. The template gates the Cura tab on
   * `caps.curaAssistant` and `markAsReviewedIfNeeded()` gates on `caps.autoMarkReviewed`;
   * nothing re-derives those from `readOnly`.
   */
  caps: LabResultsDialogCapabilities = labResultsDialogCapabilities(false);
  unlinkedResult: UnlinkedResultDetail | null = null;
  unlinkedGroupId: string | null = null;
  /** Three-state render decision for an unlinked document - see `unlinkedResultsDisplayState`. */
  unlinkedState: UnlinkedResultsDisplayState = 'results';
  unlinkedStateTitle = '';
  unlinkedStateDetail = '';
  /** Whether the test tables should render at all. Unchanged from `hasResultsFlag` outside read-only. */
  showResultsBody = false;

  /**
   * The four parties the lab named, already suppressed and filtered down to renderable slots.
   * Empty — which is every pre-existing row — means the header emits no badge container at all.
   * Computed once in the constructor: this component is OnPush and the parties never change.
   */
  vendorDoctorBadges: ReadonlyArray<VendorDoctorBadge> = [];

  // Pre-computed values for performance
  totalTests: number = 0;
  abnormalResults: number = 0;
  hasResultsFlag: boolean = false;
  computedTestResults: any[] = [];
  requisitionNumber: string | null = null;

  // Test filter
  testFilterValue: string = '';

  // Flagging properties
  isFlagged: boolean = false;

  // AI Chat properties
  messages: { id: number; message: string; prompt?: string; createdAt: Date; isSent: boolean; sender: any; isLoading: boolean; isEditing?: boolean, isCopied?: boolean; }[] = [];
  newMessage: string = '';
  chatLoading: boolean = false;
  lockProcessing: boolean = false;
  private subscriptions: Subscription[] = [];
  private sendMessageTimeout: any;

  // User and permissions
  userProfile: any;
  canSign: boolean = false;
  isPhysician: boolean = false;
  groupUsers: any[] = [];
  hasSignaturePermission: boolean = false;
  tokenBalance?: number;
  isPremium: boolean = false;

  // Email functionality
  sendingDetails = { patchPatient: false, search: '', name: '', email: '' };
  includeSignatureOnPdf: boolean = false;
  selectedDoctor: any = {};
  pdfDate: Date = new Date();

  // Socket and AI properties
  socketId: string;

  hasFullMessageResponse: boolean = false;

  // Note type selection for Cura
  noteTypes: any[] = [];
  forCuraNoteTypes: any[] = [];
  selectedNoteTypeId: string | null = null;
  selectedNoteTypeLabel: string = '';
  messagePlaceholder: string = 'Type your message...';

  // Note management properties
  selectedProcedureCodes: any[] = [];
  noteMediaObjects: FileObject[] = [];
  addingNote: any = {};
  todayDate: moment.Moment = moment();
  public addNoteDialogRef!: MatDialogRef<any>;

  /** A state unit (tasks/state-mode.md) records ICD-10 codes only. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    public dialogRef: MatDialogRef<LabResultsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LabResultsDialogData,
    @Inject(PLATFORM_ID) private platformId: object,
    private patientLabService: PatientLabService,
    private aiService: AiAssistantService,
    private patientService: PatientService,
    private communicationService: PatientCommunicationService,
    private pdfService: PdfDownloadService,
    private speechService: SpeechRecognitionService,
    private userService: UserService,
    private groupService: GroupService,
    private socketService: SocketService,
    private usageService: TokenUsageService,
    private noteService: PatientNoteService,
    private noteTypeService: NoteTypeService,
    private colleagueService: ColleagueService,
    private router: Router,
    public override feedbackService: FeedbackService,
    public override dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private fileDownloadService: FileDownloadService,
    private clipboardService: ClipboardService,
    private resultsService: ResultsService
  ) {
    super(feedbackService, dialog);
    this.readOnly = data.readOnly === true;
    this.caps = labResultsDialogCapabilities(this.readOnly, isStateModeGroup(this.groupService.group));
    this.unlinkedResult = data.unlinkedResult ?? null;
    this.unlinkedGroupId = data.groupId ?? null;
    // In read-only mode there is no patient record at all. Nothing may dereference it.
    this.patient = data.patient ?? null;
    // ...and no PatientLabOrder either, so stand in a display-only object so the header
    // bindings have something to read. It is never PATCHed - `caps` sees to that.
    this.labOrder = this.readOnly ? this.buildUnlinkedDisplayOrder() : data.labOrder;
    // Computed ONCE. This component is OnPush and the parties never change while it is open,
    // so a template method would be re-run for nothing. Read from the explicit field first —
    // the read-only path has no lab order to read it off — then from the order itself.
    this.vendorDoctorBadges = vendorDoctorBadges(
      { vendorDoctors: data.vendorDoctors ?? data.labOrder?.vendorDoctors ?? null },
      data.vendorDoctorPlatformCode ?? null
    );
    this.socketId = this.generateRandomSocketId(25);
  }

  /** Display-only stand-in for `labOrder` when viewing an unlinked result. Never persisted. */
  private buildUnlinkedDisplayOrder(): any {
    const unlinked = this.unlinkedResult;
    return {
      id: unlinked?.id || '',
      orderNumber: unlinked?.specimenNumber || null,
      resultDate: unlinked?.receivedAt || null,
      status: 'unlinked',
      flagged: false,
      reviewedAt: null
    };
  }

  /** "Surname, First name" exactly as the laboratory sent it. This is not a patient record. */
  get unlinkedSubjectName(): string {
    const demographics = this.unlinkedResult?.demographics;
    const surname = (demographics?.surname || '').trim();
    const firstName = (demographics?.firstName || '').trim();
    const joined = [surname, firstName].filter(Boolean).join(', ');
    return joined || 'Unknown patient';
  }

  /** The PDF is the actual document, so it stays available even with nothing parsed to show. */
  get downloadDisabled(): boolean {
    if (!this.caps.download) {
      return true;
    }
    if (this.readOnly) {
      return this.loading;
    }
    return !this.hasResultsFlag || this.loading;
  }

  ngOnInit(): void {
    this.loadLabResults();
    this.initializeUserProfile();
    this.checkIfFlagged();

    // Check if group is premium (unlimited tokens, no warnings)
    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((group: any) => {
        this.isPremium = group?.isPremium === true;
        // Update lockProcessing - always false if premium
        if (this.isPremium) {
          this.lockProcessing = false;
        } else if (this.usageService.tokenBalanceSnapshot < 0) {
          this.lockProcessing = true;
        } else {
          this.lockProcessing = false;
        }
        this.cdr.markForCheck();
      });

    // Subscribe to token balance changes
    this.usageService.tokenBalance$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.tokenBalance = result;
        // Update lockProcessing - always false if premium
        if (!this.isPremium) {
          if (this.usageService.tokenBalanceSnapshot < 0) {
            this.lockProcessing = true;
          } else {
            this.lockProcessing = false;
          }
        }
        this.cdr.markForCheck();
      });

    // Join AI assistant channel (never in read-only mode - the Cura tab does not exist there)
    if (this.caps.curaAssistant) {
      this.socketService.emit('joinAIAssistant', { socketId: this.socketId });
    }

    // Load forCura note types
    this.noteTypeService.noteTypes$
      .pipe(takeUntil(this.destroy$))
      .subscribe((noteTypes) => {
        if (noteTypes) {
          this.forCuraNoteTypes = noteTypes.filter((nt: any) => nt.forCura);
          // Update display if a note type was already selected
          if (this.selectedNoteTypeId) {
            this.updateNoteTypeDisplay();
          }
          this.cdr.markForCheck();
        }
      });
  }

  ngAfterViewInit(): void {
    // Set height dynamically after Material has finished rendering
    // This ensures the tab content fills the available height
    setTimeout(() => {
      const tabContent = document.querySelector('.lab-results-dialog .tab-content') as HTMLElement;
      const dialogContent = document.querySelector('.lab-results-dialog .dialog-content') as HTMLElement;
      const tabBodyContent = document.querySelector('.lab-results-dialog .mat-tab-body-content') as HTMLElement;

      if (dialogContent && tabBodyContent) {
        const dialogContentHeight = dialogContent.offsetHeight;
        if (dialogContentHeight > 0) {
          // Set height on tab body content to fill available space
          tabBodyContent.style.height = `${dialogContentHeight}px`;
          tabBodyContent.style.minHeight = `${dialogContentHeight}px`;

          if (tabContent) {
            tabContent.style.height = `${dialogContentHeight}px`;
            tabContent.style.minHeight = `${dialogContentHeight}px`;
          }
        }
      }
    }, 0);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.noteMediaObjects = [];
    this.selectedProcedureCodes = [];
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.sendMessageTimeout) {
      clearTimeout(this.sendMessageTimeout);
    }
    // Leave AI assistant channel (only if we joined it)
    if (this.caps.curaAssistant) {
      this.socketService.emit('leaveAIAssistant', { socketId: this.socketId });
    }
  }

  loadLabResults(): void {
    this.loading = true;
    this.error = null;

    if (this.readOnly) {
      // There is no PatientLabOrder to re-fetch. The caller already pulled the parsed document
      // from GET api/results/group/:groupId/unlinked/:id/results, which returns labResults in
      // exactly the same shape, so computeLabResultsData() below works unchanged.
      this.labResults = this.unlinkedResult?.labResults ?? null;
      this.computeLabResultsData();
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }

    this.patientLabService.getLabOrderById(this.labOrder.id)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.labResults = response.data.labResults;
            // Update labOrder with latest data from API
            if (response.data) {
              this.labOrder = { ...this.labOrder, ...response.data };
            }
            this.checkIfFlagged();
            this.computeLabResultsData();
            this.loading = false;
            this.cdr.markForCheck();
            // Mark as reviewed after lab results are loaded (if not already reviewed)
            this.markAsReviewedIfNeeded();
          } else {
            this.error = 'Failed to load lab results';
            this.loading = false;
            this.cdr.markForCheck();
          }
        },
        error: (error) => {
          console.error('Error loading lab results:', error);
          this.error = 'Error loading lab results';
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private computeLabResultsData(): void {
    if (!this.labResults) {
      this.hasResultsFlag = false;
      this.totalTests = 0;
      this.abnormalResults = 0;
      this.computedTestResults = [];
      this.requisitionNumber = null;
      this.finaliseDisplayState();
      return;
    }

    this.hasResultsFlag = this.labResults.specimens && this.labResults.specimens.length > 0;

    if (!this.hasResultsFlag) {
      this.totalTests = 0;
      this.abnormalResults = 0;
      this.computedTestResults = [];
      this.requisitionNumber = null;
      this.finaliseDisplayState();
      return;
    }

    // Pre-compute requisition number from the first specimen (should be same for all)
    const firstSpecimen = this.labResults.specimens[0];
    this.requisitionNumber = firstSpecimen?.requisition?.number || null;

    let totalTests = 0;
    let abnormalResults = 0;
    const computedResults: any[] = [];
    const filterLower = this.testFilterValue.toLowerCase();

    this.labResults.specimens.forEach((specimen: any, specimenIndex: number) => {
      // Pre-compute specimen-level properties
      const isMicrobiology = specimen.type === 'MIC';
      specimen.isMicrobiology = isMicrobiology;
      specimen.specimenIcon = isMicrobiology ? 'bacteria' : 'biotech';
      
      // Use procedures in the exact order provided by the lab (no sorting)
      
      // Pre-compute which tests match the filter for this specimen
      let specimenHasMatchingTests = false;
      
      specimen.procedures?.forEach((procedure: any, procedureIndex: number) => {
        // Pre-compute which tests match the filter for this procedure
        let procedureHasMatchingTests = false;
        
        procedure.tests?.forEach((test: any, testIndex: number) => {
          totalTests++;

          // For microbiology, use special flag detection (includes value-based detection)
          const flagClass = isMicrobiology 
            ? this.getMicrobiologyFlagClass(test.flag || '', test.value || '')
            : (this.getResultFlagClass(test.flag || '') || 'normal');
          const flagIcon = isMicrobiology
            ? this.getMicrobiologyFlagIcon(test.flag || '', test.value || '')
            : (this.getResultFlagIcon(test.flag || '') || 'check_circle');
          const flagText = isMicrobiology
            ? this.getMicrobiologyFlagText(test.flag || '', test.value || '')
            : (this.getResultFlagText(test.flag || '') || 'Normal');

          // Count abnormal results (for microbiology, includes value-based abnormal findings)
          const hasAbnormalFlag = isMicrobiology
            ? (flagClass === 'abnormal' || flagClass === 'high' || flagClass === 'low' || flagClass === 'critical')
            : (test.flag && test.flag.trim() !== '');
          if (hasAbnormalFlag) {
            abnormalResults++;
          }

          // Pre-compute test filter match
          const testMatchesFilter = !this.testFilterValue || this.testFilterValue.trim() === '' || 
            (test.testName || '').toLowerCase().includes(filterLower) ||
            (test.testCode || '').toLowerCase().includes(filterLower) ||
            (test.testAbbreviation || '').toLowerCase().includes(filterLower);
          
          test.matchesFilter = testMatchesFilter;
          
          if (testMatchesFilter) {
            procedureHasMatchingTests = true;
            specimenHasMatchingTests = true;
          }

          // Pre-compute comment text
          test.commentText = this.getTestCommentText(test);

          // Pre-compute display values
          test.displayName = test.testName || '(No name)';
          test.displayValue = test.value || '(No value)';
          
          // For microbiology, mark if this is a significant finding
          if (isMicrobiology) {
            test.isSignificantFinding = hasAbnormalFlag || 
              (test.value && (
                (test.value.toLowerCase().includes('present')) ||
                (test.value.toLowerCase().includes('isolated')) ||
                (test.value.toLowerCase().includes('detected')) ||
                (test.value.toLowerCase().includes('positive'))
              ));
          }

          // Update the original test object with computed flag properties
          test.flagClass = flagClass;
          test.flagIcon = flagIcon;
          test.flagText = flagText;
          test.hasAbnormalFlag = hasAbnormalFlag;
          test.isAbnormal = hasAbnormalFlag;
          test.specimenIndex = specimenIndex;
          test.procedureIndex = procedureIndex;
          test.testIndex = testIndex;

          // Also store in computed results for reference
          computedResults.push({
            ...test,
            specimenIndex,
            procedureIndex,
            testIndex,
            flagClass,
            flagIcon,
            flagText,
            hasAbnormalFlag
          });
        });
        
        // Store procedure-level computed property
        procedure.hasMatchingTests = procedureHasMatchingTests;
      });
      
      // Store specimen-level computed property
      specimen.hasMatchingTests = specimenHasMatchingTests;
    });

    this.totalTests = totalTests;
    this.abnormalResults = abnormalResults;
    this.computedTestResults = computedResults;
    this.finaliseDisplayState();
  }

  /**
   * Decides what the results pane shows.
   *
   * Outside read-only mode this is exactly the old behaviour: the tables render iff
   * `hasResultsFlag`.
   *
   * In read-only mode the server's THREE-STATE `hasResults` decides, because "parsed, and
   * there genuinely are no tests" (a comment-only blood gas report) is a real document that
   * must render its header, while "never parsed" is a different message again. Collapsing
   * the two with `!hasResults` would tell a clinician a real report was missing.
   */
  private finaliseDisplayState(): void {
    if (this.readOnly) {
      this.unlinkedState = unlinkedResultsDisplayState(this.unlinkedResult?.hasResults, this.totalTests);
      const copy = unlinkedResultsStateCopy(this.unlinkedState);
      this.unlinkedStateTitle = copy.title;
      this.unlinkedStateDetail = copy.detail;
      this.showResultsBody = this.unlinkedState === 'results';
      return;
    }
    this.showResultsBody = this.hasResultsFlag;
  }

  getResultFlagClass(flag: string | null | undefined): string {
    if (!flag || flag === null || flag === undefined || flag.trim() === '') return 'normal';

    const flagLower = flag.toLowerCase();
    if (flagLower.includes('h') || flagLower.includes('high')) return 'high';
    if (flagLower.includes('l') || flagLower.includes('low')) return 'low';
    if (flagLower.includes('c') || flagLower.includes('critical')) return 'critical';

    return 'normal';
  }

  getResultFlagIcon(flag: string | null | undefined): string {
    // Handle null, undefined, or empty string
    if (!flag || flag === null || flag === undefined || flag.trim() === '') {
      return 'check_circle';
    }

    const flagLower = flag.toLowerCase();

    if (flagLower.includes('h') || flagLower.includes('high')) {
      return 'trending_up';
    }
    if (flagLower.includes('l') || flagLower.includes('low')) {
      return 'trending_down';
    }
    if (flagLower.includes('c') || flagLower.includes('critical')) {
      return 'warning';
    }

    return 'check_circle';
  }

  getResultFlagText(flag: string | null | undefined): string {
    if (!flag || flag === null || flag === undefined || flag.trim() === '') return 'Normal';

    const flagLower = flag.toLowerCase();
    if (flagLower.includes('h') || flagLower.includes('high')) return 'High';
    if (flagLower.includes('l') || flagLower.includes('low')) return 'Low';
    if (flagLower.includes('c') || flagLower.includes('critical')) return 'Critical';

    return 'Normal';
  }

  // Microbiology-specific flag detection
  getMicrobiologyFlagClass(flag: string | null | undefined, value: string | null | undefined): string {
    if (!flag || flag === null || flag === undefined || flag.trim() === '') {
      // Check value for positive findings
      const valueLower = (value || '').toLowerCase();
      if (valueLower.includes('present') || 
          valueLower.includes('isolated') || 
          valueLower.includes('detected') ||
          valueLower.includes('positive') ||
          valueLower.includes('**')) {
        return 'abnormal';
      }
      return 'normal';
    }

    const flagTrimmed = flag.trim();
    // Microbiology flags like "**" indicate positive/abnormal findings
    if (flagTrimmed === '**' || flagTrimmed.includes('**')) {
      return 'abnormal';
    }

    // Check for other flag patterns
    const flagLower = flag.toLowerCase();
    if (flagLower.includes('h') || flagLower.includes('high')) return 'high';
    if (flagLower.includes('l') || flagLower.includes('low')) return 'low';
    if (flagLower.includes('c') || flagLower.includes('critical')) return 'critical';
    if (flagLower.includes('*')) return 'abnormal'; // Any asterisk pattern

    return 'normal';
  }

  getMicrobiologyFlagIcon(flag: string | null | undefined, value: string | null | undefined): string {
    const flagClass = this.getMicrobiologyFlagClass(flag, value);
    
    if (flagClass === 'abnormal') {
      return 'warning';
    }
    if (flagClass === 'high') {
      return 'trending_up';
    }
    if (flagClass === 'low') {
      return 'trending_down';
    }
    if (flagClass === 'critical') {
      return 'error';
    }

    return 'check_circle';
  }

  getMicrobiologyFlagText(flag: string | null | undefined, value: string | null | undefined): string {
    const flagClass = this.getMicrobiologyFlagClass(flag, value);
    
    if (flagClass === 'abnormal') {
      return 'Positive';
    }
    if (flagClass === 'high') {
      return 'High';
    }
    if (flagClass === 'low') {
      return 'Low';
    }
    if (flagClass === 'critical') {
      return 'Critical';
    }

    return 'Normal';
  }

  formatValue(value: string, units: string): string {
    if (!value) return 'N/A';
    return units ? `${value} ${units}` : value;
  }

  formatRange(range: string): string {
    if (!range || range.trim() === '') return 'N/A';
    return range;
  }

  hasResults(): boolean {
    return this.hasResultsFlag;
  }

  getTotalTests(): number {
    return this.totalTests;
  }

  getAbnormalResults(): number {
    return this.abnormalResults;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onPrint(): void {
    // TODO: Implement print functionality
    console.log('Print lab results');
  }

  async onDownload(): Promise<void> {
    if (this.readOnly) {
      // The jsPDF path prints patient demographics; there is no patient here.
      this.downloadLabResultsPdf();
      return;
    }
    if (!this.labResults || !this.hasResults()) {
      this.showToastMessage('No lab results available to download');
      return;
    }

    try {
      await this.generateLabResultsPdf();
    } catch (error) {
      console.error('Error generating lab results PDF:', error);
      this.showToastMessage('There was an error generating the PDF. Please try again.');
    }
  }

  downloadLabResultsPdf(): void {
    if (this.readOnly) {
      this.downloadUnlinkedResultPdf();
      return;
    }
    if (!this.labOrder || !this.hasResultsFlag) {
      this.showToastMessage('No lab results available to download');
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.patientLabService.downloadLabResultsAsPdf(this.labOrder.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (blob: Blob) => {
          const orderNumber = this.labOrder.orderNumber || this.labOrder.id.substring(0, 8);
          await this.fileDownloadService.download(blob, `lab-results-${orderNumber}.pdf`);
          this.loading = false;
          this.cdr.markForCheck();
          this.showToastMessage('Lab results PDF downloaded successfully');
        },
        error: (error) => {
          console.error('Error downloading lab results PDF:', error);
          this.loading = false;
          this.cdr.markForCheck();
          
          let errorMessage = 'Failed to download lab results PDF';
          if (error.error && typeof error.error === 'object' && error.error.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          this.showToastMessage(errorMessage);
        }
      });
  }

  /**
   * Server-rendered PDF for an unlinked result. Same shape as `downloadLabResultsPdf` above:
   * server call -> blob -> fileDownloadService.download, with the same loading state and the
   * same toasts. Stays enabled even when nothing was parsed, because the PDF *is* the document.
   */
  private downloadUnlinkedResultPdf(): void {
    const groupId = this.unlinkedGroupId;
    const id = this.unlinkedResult?.id;
    if (!groupId || !id) {
      this.showToastMessage('No lab results available to download');
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.resultsService.downloadUnlinkedResultPdf(groupId, id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (blob: Blob) => {
          await this.fileDownloadService.download(
            blob,
            unlinkedPdfFileName(this.unlinkedResult?.specimenNumber, id)
          );
          this.loading = false;
          this.cdr.markForCheck();
          this.showToastMessage('Lab results PDF downloaded successfully');
        },
        error: (error) => {
          console.error('Error downloading unlinked lab results PDF:', error);
          this.loading = false;
          this.cdr.markForCheck();
          this.showToastMessage(unlinkedBlobErrorMessage(error, 'Failed to download lab results PDF'));
        }
      });
  }

  private async generateLabResultsPdf(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('PDF generation is only available in browser environment');
      return;
    }

    const [jsPDFModule, autoTableModule] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);

    const { jsPDF } = jsPDFModule;
    const doc = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
    });

    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Load group logo and footer images
    const group = this.groupService.group;
    const logoImage = await this.getBase64ImageFromURL(group?.headerImageUrl || '');
    const footerImage = group?.footerImageUrl ? await this.getBase64ImageFromURL(group.footerImageUrl) : null;

    // Calculate header and footer space needed (following exact pattern from pdf-download.service)
    // Increased headerHeight to account for headers that may contain doctor info (taller than just logo)
    const headerHeight = 70; // Starting position after header (5mm top + up to 50mm header content + 15mm spacing)
    const footerHeight = footerImage ? 45 : 10; // Space needed for footer (45mm if image, 10mm if text only)
    const safeBottom = pageHeight - footerHeight; // Safe area for content (accounting for footer)

    // Set up the header with the logo image (following exact pattern from pdf-download.service)
    const headerFunction = () => {
      return new Promise<void>((resolve) => {
        if (!logoImage) {
          resolve();
          return;
        }

        const logoHeight = 35;
        const pageCenter = pageWidth / 2;

        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          const logoWidth = logoHeight * aspectRatio;

          const logoX = pageCenter - logoWidth / 2;
          const logoY = 5;
          doc.addImage(img, 'PNG', logoX, logoY, logoWidth, logoHeight);

          doc.setFontSize(16);
          doc.setTextColor('#353740');

          resolve();
        };
        img.onerror = () => resolve(); // If image fails, just continue
        img.src = logoImage.indexOf('data:') === -1 ? `data:image/png;base64,${logoImage}` : logoImage;
      });
    };

    let yPosition = headerHeight; // Start after header space (header will be drawn at end like pdf-download.service)

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('LABORATORY RESULTS', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Patient Information
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    yPosition += 5;
    doc.text(`Patient: ${this.patient.name} ${this.patient.surname}`, margin, yPosition);
    yPosition += 6;
    doc.text(`Date of Birth: ${this.patient.dob ? moment(this.patient.dob).format('DD/MM/YYYY') : 'N/A'}`, margin, yPosition);
    yPosition += 6;
    if (this.requisitionNumber) {
      doc.text(`Requisition Number: ${this.requisitionNumber}`, margin, yPosition);
      yPosition += 6;
    }
    if (this.labOrder.orderNumber) {
      doc.text(`Order Number: ${this.labOrder.orderNumber}`, margin, yPosition);
      yPosition += 6;
    }
    if (this.labOrder.resultDate) {
      doc.text(`Result Date: ${moment(this.labOrder.resultDate).format('DD/MM/YYYY HH:mm')}`, margin, yPosition);
      yPosition += 6;
    }
    yPosition += 5;

    // Process each specimen
    for (let specimenIndex = 0; specimenIndex < this.labResults.specimens.length; specimenIndex++) {
      const specimen = this.labResults.specimens[specimenIndex];
      const isMicrobiology = specimen.type === 'MIC';
      
      // Check if we need a new page (account for footer space)
      if (yPosition > safeBottom) {
        doc.addPage();
        yPosition = headerHeight; // Reset to header height (header will be drawn at end)
      }

      // Specimen Header
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Specimen ${specimenIndex + 1}${specimen.department ? ` - ${specimen.department}` : ''}${isMicrobiology ? ' (Microbiology)' : ''}`, margin, yPosition);
      yPosition += 8;

      // Specimen Details
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      if (specimen.number) {
        doc.text(`Specimen Number: ${specimen.number}`, margin, yPosition);
        yPosition += 5;
      }
      if (specimen.collectionDate) {
        const collectionTime = specimen.collectionTime ? ` at ${specimen.collectionTime}` : '';
        doc.text(`Collection Date: ${moment(specimen.collectionDate).format('DD/MM/YYYY')}${collectionTime}`, margin, yPosition);
        yPosition += 5;
      }
      if (isMicrobiology && specimen.source) {
        doc.text(`Source: ${specimen.source}`, margin, yPosition);
        yPosition += 5;
      }
      if (isMicrobiology && specimen.description) {
        doc.text(`Description: ${specimen.description}`, margin, yPosition);
        yPosition += 5;
      }
      yPosition += 3;

      // Process procedures
      if (specimen.procedures) {
        for (let procedureIndex = 0; procedureIndex < specimen.procedures.length; procedureIndex++) {
          const procedure = specimen.procedures[procedureIndex];
          // Procedure Name - add space above
          // Check if we need a new page (account for footer space)
          if (yPosition > safeBottom) {
            doc.addPage();
            yPosition = headerHeight; // Reset to header height (header will be drawn at end)
          }

          yPosition += 5; // Space above procedure name
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(procedure.procedureName || procedure.procedureCode || 'Procedure', margin, yPosition);
          yPosition += 7;

          // Build table data for tests
          const tableData: any[] = [];

          if (procedure.tests) {
            for (let testIndex = 0; testIndex < procedure.tests.length; testIndex++) {
              const test = procedure.tests[testIndex];
              if (isMicrobiology) {
                // Microbiology format: Test Name | Value | Comment
                const commentText = this.getTestCommentText(test);
                tableData.push([
                  test.testName || test.testCode || 'N/A',
                  test.value || 'N/A',
                  commentText || '-'
                ]);
              } else {
                // Regular lab format: Test Name | Value | Units | Reference Range | Flag
                const flagText = this.getResultFlagText(test.flag || '');
                const valueWithUnits = test.units ? `${test.value || 'N/A'} ${test.units}` : (test.value || 'N/A');
                tableData.push([
                  test.testName || test.testCode || 'N/A',
                  valueWithUnits,
                  test.range || '-',
                  flagText
                ]);
              }
            }
          }

          if (tableData.length > 0) {
            // Check if table will fit on current page before starting
            if (yPosition > safeBottom - 20) { // Reserve at least 20mm for table header
              doc.addPage();
              yPosition = headerHeight; // Reset to header height (header will be drawn at end)
            }

            // Add table
            const tableConfig: any = {
              startY: yPosition,
              head: isMicrobiology 
                ? [['Test Name', 'Result', 'Comment']]
                : [['Test Name', 'Value', 'Reference Range', 'Flag']],
              body: tableData,
              theme: 'striped',
              styles: {
                font: 'helvetica',
                fontSize: 9,
                cellPadding: 3,
                overflow: 'linebreak',
              },
              headStyles: {
                fillColor: [0, 150, 136], // MedicAI primary color (#009688)
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'left',
              },
              columnStyles: isMicrobiology ? {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 'auto' }
              } : {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 'auto', halign: 'center' },
                2: { cellWidth: 'auto', halign: 'center' },
                3: { cellWidth: 'auto', halign: 'center' }
              },
              margin: { 
                top: 0,
                left: margin, 
                right: margin, 
                bottom: footerHeight + 5 
              },
              tableWidth: pageWidth - (margin * 2),
              pageBreak: 'auto',
              didDrawPage: (data: any) => {
                // Update yPosition after table is drawn
                yPosition = data.cursor!.y;
              }
            };

            // Color code abnormal results
            if (!isMicrobiology) {
              tableConfig.bodyStyles = (row: any, data: any) => {
                const test = procedure.tests[row - 1];
                if (test && test.flag && test.flag.trim() !== '') {
                  const flagClass = this.getResultFlagClass(test.flag);
                  if (flagClass === 'high' || flagClass === 'low' || flagClass === 'critical') {
                    return { fillColor: [255, 235, 235] }; // Light red for abnormal
                  }
                }
                return {};
              };
            }

            autoTableModule.default(doc, tableConfig);
            yPosition = (doc as any).lastAutoTable.finalY + 8; // Space under each table
          }
        }
      }
    }

    // Set up the footer with page numbers and the "Generated by" message (following exact pattern from pdf-download.service)
    const footerFunction = (pageNumber: number, totalPages: number) => {
      return new Promise<void>((resolve) => {
        const user = this.userService.user;
        const currentDateTime = formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss', 'en-US');

        if (footerImage) {
          const footerImageHeight = 35;
          const pageCenter = pageWidth / 2;

          const img = new Image();
          img.onload = () => {
            const aspectRatio = img.width / img.height;
            const footerImageWidth = footerImageHeight * aspectRatio;

            const imgX = pageCenter - footerImageWidth / 2;
            const imgY = doc.internal.pageSize.getHeight() - 45;
            doc.addImage(img, 'PNG', imgX, imgY, footerImageWidth, footerImageHeight);

            doc.setFontSize(8);
            doc.text(`Generated by ${user.title} ${user.name} ${user.surname} using MedicAI (contact@medicai.co.za) on ${currentDateTime}`, 10, doc.internal.pageSize.getHeight() - 5);
            doc.text(`Page ${pageNumber} of ${totalPages}`, doc.internal.pageSize.getWidth() - 10, doc.internal.pageSize.getHeight() - 5, { align: 'right' });
            resolve();
          };
          img.onerror = () => {
            // If image fails to load, just show text
            doc.setFontSize(8);
            doc.text(`Generated by ${user.title} ${user.name} ${user.surname} using MedicAI (contact@medicai.co.za) on ${currentDateTime}`, 10, doc.internal.pageSize.getHeight() - 10);
            doc.text(`Page ${pageNumber} of ${totalPages}`, doc.internal.pageSize.getWidth() - 10, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
            resolve();
          };
          img.src = footerImage.indexOf('data:') === -1 ? `data:image/png;base64,${footerImage}` : footerImage;
        } else {
          doc.setFontSize(8);
          doc.text(`Generated by ${user.title} ${user.name} ${user.surname} using MedicAI (contact@medicai.co.za) on ${currentDateTime}`, 10, doc.internal.pageSize.getHeight() - 10);
          doc.text(`Page ${pageNumber} of ${totalPages}`, doc.internal.pageSize.getWidth() - 10, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
          resolve();
        }
      });
    };

    // Apply the header and footer to each page (following exact pattern from pdf-download.service)
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      await headerFunction();
      await footerFunction(i, totalPages);
    }

    // Download PDF via platform-aware service
    const filename = `Lab_Results_${this.patient.name}_${this.patient.surname}_${moment().format('YYYYMMDD_HHmmss')}`;
    const blob = doc.output('blob') as Blob;
    await this.fileDownloadService.download(blob, `${filename}.pdf`);
  }

  private async getBase64ImageFromURL(url: string): Promise<string> {
    if (!url) {
      return '';
    }
    try {
      const proxyUrl = `${environment.API_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl);
      const data = await response.json();
      return data.base64Image || '';
    } catch (error) {
      console.error('Error loading image:', error);
      return '';
    }
  }

  // AI Chat Methods
  private initializeUserProfile(): void {
    this.userService.userProfile$.subscribe({
      next: (profile) => {
        this.userProfile = profile;
        
        // Mark as reviewed after profile is loaded
        this.markAsReviewedIfNeeded();
      },
      error: (error) => {
        console.error('Error loading user profile:', error);
      }
    });

    // Use the same approach as ai-assistant-dialog
    this.isPhysician = this.userService.isUserPhysician;
    this.canSign = this.userService.canUserSign;
    this.hasSignaturePermission = this.userService.isNonPhysician;
    this.groupUsers = this.userService.availableDoctorsList;
  }

  generatePatientSummary(): void {
    if (!this.caps.curaAssistant) return;
    const prompt = `Generate a patient-friendly summary of these lab results for ${this.patient.name} ${this.patient.surname}. 
    Explain the results in simple terms that a patient can understand, highlighting any abnormal values and what they might mean. 
    Keep the language clear and avoid medical jargon.`;
    this.sendMessage({ message: prompt, isQuickAction: true });
  }

  explainAbnormalResults(): void {
    if (!this.caps.curaAssistant) return;
    const prompt = `Explain any abnormal results in these lab tests in patient-friendly language. 
    Focus on what these results might indicate and what the patient should know about them.`;
    this.sendMessage({ message: prompt, isQuickAction: true });
  }

  generateFollowUpRecommendations(): void {
    if (!this.caps.curaAssistant) return;
    const prompt = `Based on these lab results, provide follow-up recommendations for the patient. 
    Include any lifestyle changes, medications, or additional tests that might be needed. 
    Explain these recommendations in patient-friendly terms.`;
    this.sendMessage({ message: prompt, isQuickAction: true });
  }

  sendMessage(action?: any): void {
    // Cura posts `patientId` and cannot work without a patient.
    if (!this.caps.curaAssistant) return;
    if (this.lockProcessing) return;

    // If a note type is selected, generate prompt from note type
    let prompt: string | undefined;
    if (this.selectedNoteTypeId && !action?.prompt) {
      const selectedNoteType = this.forCuraNoteTypes.find(nt => nt.id === this.selectedNoteTypeId);
      if (selectedNoteType) {
        prompt = this.generateCuraPromptFromNoteType(selectedNoteType, this.newMessage.trim());
        this.selectedNoteTypeId = null; // Reset selection after use
        this.updateNoteTypeDisplay(); // Update display properties
      }
    }

    const messageText = action?.message || this.newMessage.trim();
    if (!messageText && !prompt) return;

    const userMessage = {
      id: Date.now(),
      message: messageText || 'Generate content using the selected note type template.',
      prompt: prompt || action?.prompt || messageText,
      isSent: true,
      sender: 'user',
      isLoading: false,
      createdAt: new Date()
    };

    this.messages.push(userMessage);
    this.newMessage = '';

    const aiMessage = {
      id: Date.now() + 1,
      message: '',
      isSent: false,
      sender: 'assistant',
      isLoading: true,
      createdAt: new Date()
    };

    this.messages.push(aiMessage);
    this.cdr.markForCheck();
    this.scrollToBottom();

    // Generate AI response
    this.generateAIResponse(userMessage.prompt || messageText, aiMessage, userMessage);
  }

  private generateAIResponse(prompt: string, aiMessage: any, userMessage?: any): void {
    const currentLabResults = this.buildLabResultsContext();
    const requiresHistorical = this.requiresHistoricalData(prompt || '');

    const maxAttempts = 3;
    let attempt = 0;
    let outputStream = '';

    // Create an observable that sends the message and listens for the response.
    const sendAndListen$ = new Observable<any>(observer => {
      // (Re)send the socket message for each attempt.
      this.socketService.emit(`aiAssistant-send`, {
        channel: this.socketId,
        message: { message: userMessage?.message || prompt, prompt: userMessage?.prompt || prompt },
        responseId: aiMessage.id,
        patientId: this.patient.id,
        userId: this.userService.user.id,
        messages: this.messages || [],
        // Add lab results dialog specific data
        source: 'lab-results-dialog',
        currentLabResults: currentLabResults, // Current lab results data
        requiresHistoricalLabResults: requiresHistorical // Flag to load historical data
      });
      console.log(`Message sent (attempt ${attempt + 1})`);

      // Listen for responses from the server.
      const sub = this.socketService.listen(`aiAssistant-${this.socketId}-message`, 'lab-results-dialog')
        .subscribe({
          next: (output) => {
            outputStream += output.results || '';
            observer.next({ output, outputStream });
            // When the server signals completion, finish the observable.
            if (output.completed) {
              observer.complete();
              this.hasFullMessageResponse = true;
            }
          },
          error: (err) => observer.error(err)
        });

      return () => sub.unsubscribe();
    });

    sendAndListen$
      .pipe(
        // Wait for a response within 10 seconds; if nothing comes through, error.
        timeout(10000),
        // If a timeout or error occurs, wait 1 second, re-send, and retry (up to maxAttempts).
        retryWhen(errors =>
          errors.pipe(
            tap(err => {
              attempt++;
              // Clear the partial response so new data starts fresh.
              outputStream = '';
              // Update the UI message to let the user know a retry is occurring.
              aiMessage.message = `Retrying... (attempt ${attempt} of ${maxAttempts})`;
              if (attempt < maxAttempts) {
                // Rejoin the assistant channel before retrying
                this.socketService.emit('joinAIAssistant', { socketId: this.socketId });
                console.warn(`No response received, retrying (attempt ${attempt + 1})...`);
              } else {
                console.error('Max retry attempts reached.');
                throw err;
              }
            }),
            delay(1000)
          )
        ),
        takeUntil(this.destroy$),
        // In case retries are exhausted, handle the error.
        catchError(err => {
          return throwError(err);
        })
      )
      .subscribe({
        next: (data) => {
          // Update the assistant's message as new output comes in.
          // If the current message indicates a retry, clear the accumulated output.
          if (aiMessage.message && aiMessage.message.startsWith('Retrying')) {
            outputStream = '';
          }
          outputStream += data.results || '';
          aiMessage.message = outputStream;
          this.cdr.markForCheck();
          this.scrollToBottom();
        },
        error: (err) => {
          // After max attempts, display an error in the assistant's message.
          aiMessage.message = 'Error: Response timed out. Please try again later.';
          aiMessage.isLoading = false;
          this.chatLoading = false;
          this.cdr.markForCheck();
        },
        complete: () => {
          // On successful completion, mark the assistant's message as finished.
          aiMessage.isLoading = false;
          this.chatLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private buildLabResultsContext(): any {
    if (!this.labResults || !this.hasResults()) {
      return null;
    }

    // Return the structured lab results data instead of a string
    // This will be mapped by the server using the same mapping function
    return this.labResults;
  }

  private requiresHistoricalData(prompt: string): boolean {
    const trendKeywords = [
      'trend', 'trends', 'trending', 'history', 'historical', 'over time', 'over the past',
      'previous', 'earlier', 'past results', 'compare', 'comparison', 'change', 'changes',
      'improving', 'worsening', 'getting better', 'getting worse', 'stable', 'fluctuation',
      'pattern', 'patterns', 'evolution', 'progression', 'regression', 'baseline',
      'since', 'from', 'across', 'multiple', 'all results', 'all orders', 'all tests'
    ];

    const lowerPrompt = prompt.toLowerCase();
    return trendKeywords.some(keyword => lowerPrompt.includes(keyword));
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    }, 100);
  }

  onEnterKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  startVoiceInput(): void {
    // TODO: Implement voice input when speech service is available
    console.log('Voice input not yet implemented');
  }


  searchRecipients(): void {
    // Implement recipient search logic
  }

  sendEmail(): void {
    if (!this.caps.email) {
      return;
    }
    if (!this.sendingDetails.email || !this.sendingDetails.name) {
      alert('Please provide recipient name and email');
      return;
    }

    const lastMessage = this.messages[this.messages.length - 1];
    if (!lastMessage || lastMessage.isSent) {
      alert('No AI message to send');
      return;
    }

    // TODO: Implement email sending when service is available
    console.log('Sending email:', {
      to: this.sendingDetails.email,
      toName: this.sendingDetails.name,
      subject: `Lab Results Summary - ${this.patient.name} ${this.patient.surname}`,
      content: lastMessage.message
    });
    alert('Email functionality to be implemented');
    this.sendingDetails = { patchPatient: false, search: '', name: '', email: '' };
  }

  private generateRandomSocketId(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  // Message action methods
  toggleEditing(message: any): void {
    message.isEditing = !message.isEditing;
    this.cdr.markForCheck();
  }

  copyMessage(message: any): void {
    this.clipboardService.copyMarkdown(message.message).then(ok => {
      if (ok) {
        message.isCopied = true;
        setTimeout(() => { message.isCopied = false; }, 5000);
      }
    });
  }

  async addMessageToTimeline(message: any): Promise<void> {
    if (!this.caps.curaAssistant) {
      return;
    }
    const content = message.message;

    const dialogResult = await this.openAddNoteDialog(content);

    if (!dialogResult) return;

    const { date, noteTypeId, title, notes, isProcedure, isBilling, procedureCodes } = dialogResult;

    if (!date || !title) {
      this.showToastMessage('Date and Procedure/Title are required to add a note');
      return;
    }

    const patient = this.patientService.patient;
    const newMediaFiles = this.noteMediaObjects.filter(obj => !obj.id);

    let noteData = {
      date: date.utc().startOf('day').toISOString(),
      groupId: patient.groupId,
      patientId: patient.id,
      noteType: noteTypeId,
      noteTypeHeadings: '',
      isProcedure: isProcedure,
      isBilling: isBilling || false,
      procedureCodes: procedureCodes || [],
      title: title,
      notes: notes,
      existingMedia: []
    };

    message.message = notes;

    const formData = new FormData();
    formData.append('data', JSON.stringify(noteData));

    const mediaAppendPromises = newMediaFiles.map((obj, index) => {
      return fetch(obj.url)
        .then(response => response.blob())
        .then(blob => {
          formData.append(`newMedia${index}`, blob, `${moment().unix()}.${obj.type.split('/')[1]}`);
        });
    });

    Promise.all(mediaAppendPromises)
      .then(() => {
        this.noteService.addNote(formData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (result) => {
              this.showToastMessage('Patient note saved successfully');
              this.cdr.markForCheck();
            },
            error: (error) => {
              console.error('Error saving patient note: ', error);
              this.showToastMessage('There was an error saving the patient note. Please try again.');
            }
          });
      });
  }

  downloadAsPdf(event: MouseEvent, message: any): void {
    if (!this.caps.curaAssistant) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const filename = `MedicAI_${this.patient.name} ${this.patient.surname}_Lab_Results_Summary_${moment().valueOf()}`;

    if (this.includeSignatureOnPdf && this.isPhysician) {
      const appendSignatureMessage = message.message;
      this.pdfService.downloadPdf({
        content: appendSignatureMessage,
        filename: filename,
        patient: this.patient,
        images: [],
        attachSignature: this.includeSignatureOnPdf,
        signatureUrl: this.userProfile.signatureImageUrl,
        signatoryName: `${this.userProfile.title} ${this.userProfile.name} ${this.userProfile.surname}`,
        date: this.pdfDate,
      });

    } else if (!this.isPhysician && this.hasSignaturePermission && this.includeSignatureOnPdf) {
      const appendSignatureMessage = message.message;
      this.pdfService.downloadPdf({
        content: appendSignatureMessage,
        filename: filename,
        patient: this.patient,
        images: [],
        attachSignature: this.includeSignatureOnPdf,
        signatureUrl: this.selectedDoctor.signatureImageUrl,
        signatoryName: `${this.selectedDoctor.title} ${this.selectedDoctor.name} ${this.selectedDoctor.surname}`,
        date: this.pdfDate,
      });

    } else if (!this.includeSignatureOnPdf || !this.canSign) {
      this.pdfService.downloadPdf({
        content: message.message,
        filename: filename,
        patient: this.patient,
        images: [],
        attachSignature: false,
        date: this.pdfDate,
      });
    }

    this.closeAllMenus(this.menuTriggers);
  }

  // AI Assistant methods
  patchPatientEmailDetails(): void {
    if (this.sendingDetails.patchPatient) {
      this.sendingDetails.name = `${this.patient.name} ${this.patient.surname}`;
      this.sendingDetails.email = this.patient.email;
    } else {
      this.sendingDetails.name = '';
      this.sendingDetails.email = '';
    }
  }

  handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }

  onRecipientSelected(recipient: {name: string, email: string}): void {
    this.sendingDetails.name = recipient.name;
    this.sendingDetails.email = recipient.email;
  }

  emailMessage(message: any): void {
    if (!this.caps.email) {
      return;
    }
    if (!this.sendingDetails.email) {
      this.showToastMessage('An email address was not provided. Please check the relevant email field try again.');
      return;
    }

    if (this.includeSignatureOnPdf && this.isPhysician) {
      const appendSignatureMessage = message.message;
      this.emailCommunication(this.patient.id, 'Note', this.sendingDetails.name, this.sendingDetails.email, appendSignatureMessage, '', [], true, this.userProfile.signatureImageUrl, `${this.userProfile.title} ${this.userProfile.name} ${this.userProfile.surname}`, this.pdfDate);

    } else if (!this.isPhysician && this.hasSignaturePermission && this.includeSignatureOnPdf) {
      const appendSignatureMessage = message.message;
      this.emailCommunication(this.patient.id, 'Note', this.sendingDetails.name, this.sendingDetails.email, appendSignatureMessage, '', [], true, this.selectedDoctor.signatureImageUrl, `${this.selectedDoctor.title} ${this.selectedDoctor.name} ${this.selectedDoctor.surname}`, this.pdfDate);

    } else if (!this.includeSignatureOnPdf) {
      this.emailCommunication(this.patient.id, 'Note', this.sendingDetails.name, this.sendingDetails.email, message.message, '', [], undefined, undefined, undefined, this.pdfDate);
    }
  }

  emailCommunication(userId: string, note = 'Note', name: string, email: string, content: any, param = '', arr: any, attachSignature?: boolean, signatureUrl?: string, signatoryName?: string, pdfDate?: Date | string): void {
    const plainTextContent = this.pdfService.convertToPlainText(content);
    this.communicationService.sendAIContentEmail({
      patientId: userId,
      type: note,
      name: name,
      email: email,
      content: plainTextContent,
      speciality: param,
      images: arr,
      attachSignature: attachSignature,
      signatureUrl: signatureUrl,
      signatoryName: signatoryName,
      pdfDate: pdfDate,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Email sent successfully');
          this.closeAllMenus(this.menuTriggers);
        },
        error: (error) => {
          console.error('Error sending AI email: ', error);
          this.showToastMessage('There was an error sending the email. Please try again.');
        }
      });
  }

  private markAsReviewedIfNeeded(): void {
    // An unlinked result has no PatientLabOrder, so there is nothing to mark reviewed and
    // nothing that may be PATCHed. This is a silent side effect of merely opening the dialog,
    // so it is gated first.
    if (!this.caps.autoMarkReviewed) {
      return;
    }
    // Check if results are unseen (reviewedAt is null) and user is a doctor
    if (!this.labOrder || !this.isPhysician) {
      return;
    }
    // Check if results are completed and not yet reviewed
    if ((this.labOrder.status === 'completed' || this.labOrder.status === 'cancelled') && !this.labOrder.reviewedAt) {
      // Mark as reviewed - automatically sets reviewedAt and reviewedBy
      this.patientLabService.markResultsAsReviewed(this.labOrder.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              // Update local lab order to reflect reviewed status
              this.labOrder.reviewedAt = response.data.reviewedAt;
              this.labOrder.reviewedBy = response.data.reviewedBy;
              this.cdr.markForCheck();
            }
          },
          error: (error) => {
            console.error('Error marking results as reviewed:', error);
            // Don't show error to user as this is a background operation
          }
        });
    }
  }

  // Test filter methods
  applyTestFilter(event: Event): void {
    this.testFilterValue = (event.target as HTMLInputElement).value;
    // Recompute filter matches when filter changes
    this.computeLabResultsData();
    this.cdr.markForCheck();
  }

  clearTestFilter(): void {
    this.testFilterValue = '';
    // Recompute filter matches when filter is cleared
    this.computeLabResultsData();
    this.cdr.markForCheck();
  }

  getTestCommentText(test: any): string {
    if (!test.comment) return '';
    
    // Handle both single Localization object and array of Localization objects
    if (test.comment.Localization) {
      // Single Localization object
      if (Array.isArray(test.comment.Localization)) {
        // Array of Localization objects - find English or default
        const enLocalization = test.comment.Localization.find((loc: any) => 
          loc.language === 'en' || loc.default === 'true'
        );
        return enLocalization?.Text || '';
      } else {
        // Single Localization object
        return test.comment.Localization.Text || '';
      }
    }
    
    return '';
  }

  /**
   * Sort microbiology procedures to match how doctors read them:
   * 1. Microscopy (stains like ZN, AO, Gram) - quick preliminary results
   * 2. Culture results - what grew
   * 3. Isolates - detailed identification and sensitivity for each isolate (comes after culture)
   * 4. Identification/Genotyping (PCR, GeneXpert, etc.) - molecular tests
   * 5. Sensitivity/Antimicrobial susceptibility (standalone, not part of isolate)
   * 6. Comments/Explanatory text
   */
  sortMicrobiologyProcedures(procedures: any[]): any[] {
    const procedureOrder = (proc: any): number => {
      const code = (proc.procedureCode || '').toUpperCase();
      const name = (proc.procedureName || '').toUpperCase();
      
      // Microscopy procedures (stains) - first, quick results
      if (code.includes('ZN') || code.includes('AO') || code.includes('AUROMIN') || 
          code.includes('GRAM') || code.includes('CONFIRM') ||
          name.includes('ZN') || name.includes('ACID FAST') || name.includes('STAIN') ||
          name.includes('MICROSCOPY')) {
        return 1;
      }
      
      // Culture procedures - what grew
      if (code.includes('CULTURE') || code.includes('TBCULTURE') || 
          name.includes('CULTURE')) {
        return 2;
      }
      
      // Isolate procedures - come after culture, contain identification + sensitivity
      // Check for numeric procedure codes (like "1", "2") or "PROCEDURE" followed by number
      if (code.match(/^\d+$/) || 
          code.match(/^PROCEDURE\s*\d+$/) ||
          name.match(/^PROCEDURE\s*\d+$/) ||
          name.includes('ISOLATE') ||
          (code.length <= 3 && /^\d+$/.test(code.trim()))) {
        return 3;
      }
      
      // Identification/Genotyping (PCR, GeneXpert, etc.) - molecular tests
      if (code.includes('GENE') || code.includes('PCR') || code.includes('GENOTYPE') ||
          code.includes('GENEXPERT') || code.includes('Xpert') ||
          name.includes('GENE') || name.includes('PCR') || name.includes('GENOTYPE') ||
          name.includes('IDENTIFICATION') || name.includes('MOLECULAR')) {
        return 4;
      }
      
      // Sensitivity/Antimicrobial susceptibility (standalone, not part of isolate)
      if (code.includes('SENS') || code.includes('SENSITIVITY') || code.includes('SUSCEPTIBILITY') ||
          name.includes('SENSITIVITY') || name.includes('SUSCEPTIBILITY') || name.includes('ANTIMICROBIAL')) {
        return 5;
      }
      
      // Comments/Explanatory text (usually start with NGPOS, NGNEG, etc.)
      if (code.includes('NGPOS') || code.includes('NGNEG') || code.includes('COMMENT') ||
          name.includes('COMMENT') || name.includes('EXPLAIN') || name.includes('NOTE')) {
        return 6;
      }
      
      // Default: put unknown procedures at the end
      return 7;
    };

    // Create array with original indices to preserve lab's original order
    const proceduresWithIndex = procedures.map((proc, index) => ({ proc, originalIndex: index }));
    
    return proceduresWithIndex.sort((a, b) => {
      const orderA = procedureOrder(a.proc);
      const orderB = procedureOrder(b.proc);
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      // If same order category, preserve the lab's original sequence
      return a.originalIndex - b.originalIndex;
    }).map(item => item.proc);
  }


  navigateToPatientFile(patientId: string): void {
    if (!this.caps.patientFileLink || !patientId) {
      return;
    }
    this.patientService.getPatientById(patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
        // Close the dialog after navigation
        this.dialogRef.close();
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }

  openWhatsAppDialog(message?: any): void {
    if (!this.caps.whatsapp) {
      return;
    }
    const plainTextContent = message ? this.pdfService.convertToPlainText(message.message) : '';
    const dialogRef = this.dialog.open(WhatsAppChatDialogComponent, {
      width: '900px',
      maxWidth: '90vw',
      height: '80vh',
      maxHeight: '90vh',
      data: {
        patientId: this.patient?.id,
        patient: this.patient,
        initialMessage: plainTextContent
      }
    });
  }

  /**
   * Convert message to plain text for WhatsApp dialog
   * Used by the WhatsApp dialog trigger directive
   */
  getMessagePlainText(message?: any): string {
    if (!message) {
      return '';
    }
    return this.pdfService.convertToPlainText(message.message);
  }

  generateCuraPromptFromNoteType(noteType: any, userMessage?: string): string {
    let prompt = `Generate a ${noteType.label} using the following structure:\n\n`;
    
    // Add headings and subheadings
    if (noteType.headings && noteType.headings.length > 0) {
      prompt += 'STRUCTURE:\n';
      noteType.headings.forEach((heading: any) => {
        prompt += `- ${heading.text}\n`;
        if (heading.subheadings && heading.subheadings.length > 0) {
          heading.subheadings.forEach((subheading: any) => {
            prompt += `  - ${subheading.text}\n`;
          });
        }
      });
      prompt += '\n';
    }
    
    // Add user instructions if available
    if (noteType.userPrompt) {
      prompt += `INSTRUCTIONS:\n${noteType.userPrompt}\n\n`;
    }
    
    // Add user message if provided
    if (userMessage) {
      prompt += `USER REQUEST:\n${userMessage}\n\n`;
    }
    
    prompt += 'Please generate the content following the structure above, using the headings and subheadings as a guide.';
    
    return prompt;
  }

  onNoteTypeSelected(noteTypeId: string | null): void {
    this.selectedNoteTypeId = noteTypeId;
    this.updateNoteTypeDisplay();
    this.cdr.markForCheck();
  }

  clearNoteTypeSelection(): void {
    this.selectedNoteTypeId = null;
    this.updateNoteTypeDisplay();
    this.cdr.markForCheck();
  }

  private updateNoteTypeDisplay(): void {
    if (this.selectedNoteTypeId) {
      const selectedNoteType = this.forCuraNoteTypes.find(nt => nt.id === this.selectedNoteTypeId);
      if (selectedNoteType) {
        this.selectedNoteTypeLabel = selectedNoteType.label;
        this.messagePlaceholder = `Add additional details about this ${selectedNoteType.label} request (optional)...`;
      } else {
        this.selectedNoteTypeLabel = '';
        this.messagePlaceholder = 'Type your message...';
      }
    } else {
      this.selectedNoteTypeLabel = '';
      this.messagePlaceholder = 'Type your message...';
    }
  }

  openAddNoteDialog(content: any) {
    let procedureCodes = this.selectedProcedureCodes;
    this.addingNote = { date: moment(), noteTypeId: null, title: '', isProcedure: false, isBilling: false, notes: content, procedureCodes: procedureCodes, paidCash: false, noCharge: false };
    this.addNoteDialogRef = this.dialog.open(this.addNoteDialog!, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'add-note-dialog'
    });
    return this.addNoteDialogRef.afterClosed().toPromise();
  }

  onNoteMediaUpload(files: FileObject[]) {
    const newMediaObjects = files.map(file => ({
      ...file,
      type: file.type,
    }));

    // Check for duplicates and avoid appending unnecessarily
    const uniqueMediaObjects = newMediaObjects.filter(newObj =>
      !this.noteMediaObjects.some(existingObj => existingObj.url === newObj.url)
    );

    this.noteMediaObjects = [...this.noteMediaObjects, ...uniqueMediaObjects];
    this.cdr.markForCheck();
  }

  onFilesEdited(files: any) {
    this.noteMediaObjects = files as FileObject[];
    this.cdr.markForCheck();
  }

  onNoteMediaDelete(fileObject: FileObject) {
    const index = this.noteMediaObjects.findIndex(obj => obj.url === fileObject.url);
    if (index > -1) {
      this.noteMediaObjects.splice(index, 1);
      this.cdr.markForCheck();
    }
  }

  onSelectedProcedureCodesChanged(codes: ProcedureCode[], addingNote: any) {
    if (addingNote) {
      addingNote.procedureCodes = codes;
    } else {
      this.selectedProcedureCodes = codes;
    }
    this.cdr.markForCheck();
  }

  toggleCodeStatus(note: any, type: string) {
    switch (type) {
      case 'up-front':
        if (note.paidCash) {
          note.noCharge = false;
          note.procedureCodes = note.procedureCodes.map((c: any) => { return { ...c, noCharge: false, paidCash: true } });
        }
        break;
      case 'no-charge':
        if (note.noCharge) {
          note.paidCash = false;
          note.procedureCodes = note.procedureCodes.map((c: any) => { return { ...c, noCharge: true, paidCash: false } });
        }
        break;
    }
    this.cdr.markForCheck();
  }

  // Flagging methods
  private checkIfFlagged(): void {
    this.isFlagged = this.labOrder?.flagged || false;
  }

  toggleFlag(): void {
    if (!this.caps.flag) {
      return;
    }
    if (this.isFlagged) {
      // Unflag
      this.patientLabService.unflagLabOrder(this.labOrder.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.labOrder = { ...this.labOrder, ...response.data };
              this.isFlagged = false;
              this.showToastMessage('Lab order unflagged');
              this.cdr.markForCheck();
            }
          },
          error: (error) => {
            console.error('Error unflagging lab order:', error);
            this.showToastMessage('Error unflagging lab order');
          }
        });
    } else {
      // Flag
      this.patientLabService.flagLabOrder(this.labOrder.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.labOrder = { ...this.labOrder, ...response.data };
              this.isFlagged = true;
              this.showToastMessage('Lab order flagged for follow-up');
              this.cdr.markForCheck();
            }
          },
          error: (error) => {
            console.error('Error flagging lab order:', error);
            this.showToastMessage('Error flagging lab order');
          }
        });
    }
  }

}
